import { useMemo } from "react";
import { parseISO, isAfter, isBefore } from "date-fns";
import {
  ComposedChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DASHBOARD_COLORS } from "@/lib/dashboardColors";
import { formatCurrency } from "@/utils/format";

const ForecastLegend = () => (
  <div
    className="flex w-full items-center justify-center gap-4 text-xs text-slate-600"
    data-testid="forecast-legend"
  >
    <div className="flex items-center gap-2">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: DASHBOARD_COLORS.revenue }}
      />
      Einnahmen (Netto)
    </div>
    <div className="flex items-center gap-2">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: DASHBOARD_COLORS.expenses }}
      />
      Ausgaben (Netto)
    </div>
  </div>
);

const ForecastTooltip = ({ active, payload, label, taxEstimate }) => {
  if (!active || !payload?.length) return null;
  const actualRevenue = payload.find((item) => item.dataKey === "revenueActual")?.value;
  const forecastRevenue = payload.find((item) => item.dataKey === "revenueForecast")?.value;
  const actualExpenses = payload.find((item) => item.dataKey === "expensesActual")?.value;
  const forecastExpenses = payload.find((item) => item.dataKey === "expensesForecast")?.value;
  return (
    <div className="rounded-lg border bg-white p-3 shadow-sm text-xs text-gray-700">
      <div className="font-semibold text-gray-900">{label}</div>
      {typeof actualRevenue === "number" && (
        <div>Ist Einnahmen (Netto): {formatCurrency(actualRevenue)}</div>
      )}
      {typeof forecastRevenue === "number" && (
        <div>Forecast Einnahmen (Netto): {formatCurrency(forecastRevenue)}</div>
      )}
      {typeof actualExpenses === "number" && (
        <div>Ist Ausgaben (Netto): {formatCurrency(actualExpenses)}</div>
      )}
      {typeof forecastExpenses === "number" && (
        <div>Forecast Ausgaben (Netto): {formatCurrency(forecastExpenses)}</div>
      )}
      {taxEstimate && (
        <div className="mt-2 space-y-1 text-gray-600">
          <div>Steuern: {formatCurrency(taxEstimate.total)}</div>
          <div>USt: {formatCurrency(taxEstimate.vat)}</div>
          <div>SV: {formatCurrency(taxEstimate.social_security)}</div>
          <div>Monatl. Rücklage (ESt + SV): {formatCurrency(taxEstimate.monthly_reserve)}</div>
        </div>
      )}
    </div>
  );
};

export default function ForecastChart({
  historicalRevenue = [],
  historicalExpenses = [],
  forecastData = [],
  taxEstimate,
  isLoading,
  error,
  rangeStart,
  rangeEnd,
}) {
  const gridColor = useMemo(() => {
    if (typeof document === "undefined") return "#e2e8f0";
    return document.documentElement.classList.contains("dark") ? "#334155" : "#e2e8f0";
  }, []);
  const axisColor = "#475569";

  const data = useMemo(() => {
    const map = new Map();
    historicalRevenue.forEach((point) => {
      map.set(point.date, {
        date: point.date,
        revenue: point.value,
        expenses: 0,
      });
    });
    historicalExpenses.forEach((point) => {
      const existing = map.get(point.date) || { date: point.date, revenue: 0, expenses: 0 };
      existing.expenses = point.value;
      map.set(point.date, existing);
    });

    const sortedHistorical = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
    let cumulativeRevenue = 0;
    let cumulativeExpenses = 0;

    const output = sortedHistorical.map((point) => {
      cumulativeRevenue += point.revenue || 0;
      cumulativeExpenses += point.expenses || 0;
      return {
        date: point.date,
        revenueActual: cumulativeRevenue,
        expensesActual: cumulativeExpenses,
        revenueForecast: null,
        expensesForecast: null,
      };
    });

    if (output.length > 0) {
      const lastIndex = output.length - 1;
      output[lastIndex].revenueForecast = output[lastIndex].revenueActual;
      output[lastIndex].expensesForecast = output[lastIndex].expensesActual;
    }

    const sortedForecast = [...forecastData].sort((a, b) => a.month.localeCompare(b.month));
    sortedForecast.forEach((point) => {
      cumulativeRevenue += point.revenue || 0;
      cumulativeExpenses += point.expenses || 0;
      output.push({
        date: point.month,
        revenueActual: null,
        expensesActual: null,
        revenueForecast: cumulativeRevenue,
        expensesForecast: cumulativeExpenses,
      });
    });

    let merged = output;
    if (rangeStart && rangeEnd) {
      const start = parseISO(rangeStart);
      const end = parseISO(rangeEnd);
      merged = output.filter((item) => {
        const date = parseISO(`${item.date}-01`);
        return !isBefore(date, start) && !isAfter(date, end);
      });
    }

    return merged;
  }, [forecastData, historicalExpenses, historicalRevenue, rangeEnd, rangeStart]);

  return (
    <Card data-testid="forecast-chart">
      <CardHeader>
        <CardTitle>Kumulierte Einnahmen & Ausgaben</CardTitle>
      </CardHeader>
      <CardContent className="h-80">
        {isLoading && <Skeleton className="h-full w-full" data-testid="forecast-chart-loading" />}
        {!isLoading && error && (
          <div className="text-sm text-red-600" data-testid="forecast-chart-error">
            Forecast-Daten konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !error && data.length === 0 && (
          <div className="text-sm text-gray-500" data-testid="forecast-chart-empty">
            Keine Forecast-Daten verfügbar.
          </div>
        )}
        {!isLoading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%" minHeight={280} minWidth={200}>
            <ComposedChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke={axisColor} tick={{ fill: axisColor }} />
              <YAxis
                stroke={axisColor}
                tick={{ fill: axisColor }}
                width={110}
                tickFormatter={(value) => formatCurrency(value)}
              />
              <Tooltip content={(props) => <ForecastTooltip {...props} taxEstimate={taxEstimate} />} />
              <Legend content={<ForecastLegend />} />
              <Line
                type="monotone"
                dataKey="revenueActual"
                stroke={DASHBOARD_COLORS.revenue}
                strokeWidth={2}
                dot={false}
                name="Einnahmen (Ist)"
              />
              <Line
                type="monotone"
                dataKey="revenueForecast"
                stroke={DASHBOARD_COLORS.revenue}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                name="Einnahmen (Forecast)"
              />
              <Line
                type="monotone"
                dataKey="expensesActual"
                stroke={DASHBOARD_COLORS.expenses}
                strokeWidth={2}
                dot={false}
                name="Ausgaben (Ist)"
              />
              <Line
                type="monotone"
                dataKey="expensesForecast"
                stroke={DASHBOARD_COLORS.expenses}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                name="Ausgaben (Forecast)"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}